import { action, makeObservable, observable, override, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import IFullFormPageStore from 'store/modules/full-employee-form/iFullFormPageStore';
import { BaseFullEmployeeFormDomainParams } from 'store/modules/full-employee-form/types';
import QuoteFlowDelegate from 'store/flows/quote/quoteFlowDelegate';
import { redirectToRoute } from 'routes/redirectToRoute';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import { getIMQRequiredSubInfo, getIMQSubscriberById } from 'api/operations/electionService';
import { RequiredSubInfoWithIMQ } from 'store/modules/subscriber/subscriber-imq/types';
import { getRootStoreInstance } from 'store/rootStore';
import { Subscriber } from 'types/electionService';

export interface FullEmployeeFormDomainParams extends BaseFullEmployeeFormDomainParams {
  cartId: string;
}

export default class FullFormPageStore extends IFullFormPageStore<FullEmployeeFormDomainParams> {
  public readonly flowDelegate: QuoteFlowDelegate;
  private _requiredSubInfo: RequiredSubInfoWithIMQ | null = null;

  // eslint-disable-next-line class-methods-use-this
  protected async loadSubscriber(subscriberId: string): Promise<Pick<Subscriber, 'id' | 'common' | 'custom'> | null> {
    return getIMQSubscriberById(subscriberId);
  }

  protected getPrimaryFormId(): string | undefined {
    return this._requiredSubInfo?.imq.subFormId;
  }

  protected getDependentFormId(): string | undefined {
    return this._requiredSubInfo?.imq.dependentFormId;
  }

  public override async getInitialData(): Promise<void> {
    await this.flowDelegate.getFlowInitialData();
    const domainId = runInAction(() => getRootStoreInstance().domainStoreV2.domainHeader.id);
    const requiredSubInfo = await getIMQRequiredSubInfo(domainId);
    runInAction(() => {
      this._requiredSubInfo = requiredSubInfo;
    });
    await super.getInitialData();
  }

  public get isInitialDataLoading(): boolean {
    return this.flowDelegate.isFlowDataLoading || super.isInitialDataLoading;
  }

  public onBackRedirect(): void {
    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES, {
      groupId: this._domainData.groupId,
      periodId: this._domainData.periodId,
      cartId: this._domainData.cartId,
    });
  }

  constructor(domainData: FullEmployeeFormDomainParams) {
    super(domainData);

    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: {
        groupId: domainData.groupId,
        periodId: domainData.periodId,
        cartId: domainData.cartId,
      },
      nextPageId: QuoteRouteV2Id.IMQ_ADD_EMPLOYEES,
    });

    makeObservable<FullFormPageStore, '_requiredSubInfo'>(this, {
      _requiredSubInfo: observable,
      onBackRedirect: action.bound,
      getInitialData: override,
    });
  }
}

const FullFormPageStoreContext = createContext(new FullFormPageStore({} as FullEmployeeFormDomainParams));

export const useFullFormPageStore = (): FullFormPageStore => {
  return useContext(FullFormPageStoreContext);
};

export { FullFormPageStoreContext };
